import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Flex, Spin, Typography, Statistic, theme, Skeleton } from 'antd';
import { ExclamationCircleFilled } from '@ant-design/icons';
import { AddonBillingType, isStarterPlanId } from '@prerender/billing-shared';

import { formatCentsToDollarLocale, formatToDollar } from '../../../assets/currencies';
import ButtonWithRole from '../../../components/ButtonWithRole';
import { BILLING_WRITE_REFUSED_ROLES } from '../../auth/roleCapabilities';
import { CounterBar } from '../components/CounterBar';
import { useGetPackagesQuery } from '../api/packageApiSlice';
import { createUsNumberFormat } from '../../../utils/createUsNumberFormat';
import { formatCacheRefreshInterval } from '../../../assets/dateFormatter';
import { useTrackBillingEvent, useSuggestedPlan } from '~features/billing';
import { useTrial } from '~features/FreeTrial/useTrial';
import RenderCounterAlert from './RenderCounterAlert';
import { useCurrentRenderCount, useCharbeePlanId } from '../../../hooks/usePrerenderUser';
import { getPlanActions } from '../getPlanActions';
import { useSubscriptionState } from '../PlanStatus';
import { CustomTooltip } from '~components/Tooltip';
import { dates } from '~library/dates';
import SubscriptionReactivationModal from '../components/SubscriptionReactivationModal';
import { useEvent } from '~features/events/hooks/useEvent';
import { HubSpot } from '~features/CustomerSupport';

function TrialDaysStatistic({ trialEndsAt }) {
  const today = dayjs();
  const trialEnds = dayjs(trialEndsAt);
  const diffInHours = trialEnds.diff(today, 'hour');
  const days = Math.floor(diffInHours / 24);
  const hours = diffInHours % 24;
  return (
    <Statistic title="Trial period ends in" value={`${days} days, ${hours} hours`} style={{ textAlign: 'center' }} />
  );
}

const getPrice = (user) => {
  if (user.customDeal) {
    return formatToDollar(user.customDeal.price / 100);
  }

  if (typeof user.customPrice === 'number') {
    return formatToDollar(user.customPrice);
  }

  return formatToDollar(user.costPerMonth);
};

export const BillingOverviewPlan = ({
  user,
  isUserOnCustomPlan = false,
  showBillingPageLink = false,
  showAccountDetails = false,
}) => {
  const currentCount = useCurrentRenderCount();
  const chargebeePlanId = useCharbeePlanId();
  const [planState, paymentMethodState, subscriptionState] = useSubscriptionState();

  const { isInTrial, trialEndsAt, discountAvailable } = useTrial();
  const suggestedPlan = useSuggestedPlan();
  const { data: packagesData, isFetching: packagesLoading, error: packagesError } = useGetPackagesQuery();
  const trackBillingEvent = useTrackBillingEvent();
  const [planReactivationModalOpen, setPlanReactivationModalOpen] = React.useState(false);
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const secondaryTextStyle = { color: token.colorTextSecondary };
  const strongStyle = { fontWeight: token.fontWeightStrong };
  const detailsListStyle = { marginTop: token.marginSM, paddingLeft: token.paddingLG };
  const { track } = useEvent();
  const planActions = getPlanActions({
    planStatus: planState.status,
    isChargeable: paymentMethodState.isChargeable,
    isInTrial,
    isStarterPlan: isStarterPlanId(chargebeePlanId),
  });

  const onUpgradeLinkClick = (ctaName) => {
    trackBillingEvent('UpgradeClicked', {
      cta_location: 'Render Counter',
      cta_name: ctaName,
    });
    navigate(
      suggestedPlan ? `/billing/plans/?selectedPlan=${suggestedPlan.id}&from=overview` : '/billing/plans?from=overview'
    );
  };

  const onReactivatePlanClick = () => {
    trackBillingEvent('AccountReactivationStarted', {
      subscription_plan: user.planName,
      overdue_amount: subscriptionState.totalDues,
      location: 'billing_overview_plan',
    });

    if (subscriptionState.hasHierarchies) {
      HubSpot.openHubSpotForm();
      return;
    }

    if (!paymentMethodState.isChargeable) {
      navigate(`/billing/plans/?selectedPlan=${chargebeePlanId}&from=overview`);
    } else {
      setPlanReactivationModalOpen(true);
    }
  };

  const onKeepAccessClick = () => {
    trackBillingEvent('KeepAccessClicked', {
      subscription_plan: chargebeePlanId,
      location: 'billing_overview_plan',
    });
    navigate(`/billing/plans/?selectedPlan=${chargebeePlanId}&from=overview`);
  };

  const onActivateStarterNowClick = () => {
    // Routes to the plans page with the current Starter plan pre-selected, opening the confirmation
    // modal. The modal itself decides whether to show "Activate after trial" based on whether payment
    // was already on file when it opened (PRE-2726 QA).
    navigate(`/billing/plans/?selectedPlan=${chargebeePlanId}&from=overview`);
  };

  const onCancelPlanClick = () => {
    track('Billing: Cancelation Started', {
      subscription_plan: chargebeePlanId,
    });
    navigate('/billing/plans/cancel-plan');
  };

  const onCustomDealClick = () => {
    navigate('/custom-deal');
  };

  const afterReactivate = () => {
    track('Account Reactivated', { subscription_plan: user.planName, overdue_amount: subscriptionState.totalDues });
  };

  const activePackages = useMemo(
    () => packagesData?.data.filter((p) => p.isActive && p.billingType === AddonBillingType.Subscription),
    [packagesData]
  );
  const shouldShowPackagesSection = packagesLoading || packagesError || Boolean(activePackages?.length);
  const customDealBeginsAt = dates.dayOfPointInTime(user.customDeal?.starts_at);
  const customRenewalDateAt = dates.dayOfPointInTime(user.customDeal?.ends_at);

  return (
    <>
      <SubscriptionReactivationModal
        open={planReactivationModalOpen}
        onCancel={() => setPlanReactivationModalOpen(false)}
        afterReactivate={afterReactivate}
        planName={user.planName}
        discountAvailable={discountAvailable}
      />

      <Flex justify="space-between" align="center">
        <Typography.Title level={4}>
          {planState.status !== 'active' && (
            <CustomTooltip message={planState.warningMessage} placement="top">
              <ExclamationCircleFilled
                style={{
                  marginRight: token.marginXS,
                  color:
                    planState.recachingDisabled || planState.status === 'cancelled'
                      ? token.colorError
                      : token.colorWarning,
                }}
              />
            </CustomTooltip>
          )}
          <span>{`${user.planName} Plan`}</span>
        </Typography.Title>
        {!user.isTrial && <Typography.Text style={{ marginBottom: 10 }}>{getPrice(user)}</Typography.Text>}
      </Flex>
      {showBillingPageLink && (
        <Typography.Text>
          You can read more about your current pricing plan <Link to="/billing/overview">here</Link>
        </Typography.Text>
      )}
      <div style={{ marginTop: 16 }}>
        <RenderCounterAlert />
      </div>
      <div>
        {isUserOnCustomPlan ? (
          <>
            <Flex align="center" justify="space-between">
              <h4>Render Counter</h4>
              {currentCount.toLocaleString()}
            </Flex>
            <Flex align="center" justify="space-between">
              <h4>Renders Included</h4>
              {user.customDeal ? Number(user.customDeal.included_renders).toLocaleString('en-US') : '-'}
            </Flex>
            <Flex align="center" justify="space-between">
              <h4>Renewal date</h4>
              {customRenewalDateAt}
            </Flex>
          </>
        ) : (
          <CounterBar count={currentCount} limit={user.plan.limit} amountOf429Renders={user.amountOf429Renders} />
        )}
      </div>
      <div style={{ marginTop: 12, marginBottom: 8 }}>
        {isUserOnCustomPlan && (
          <>
            <p style={{ marginTop: token.marginSM }}>
              <span style={secondaryTextStyle}>
                Your plan and billing details are determined by a custom agreement with Prerender, created on{' '}
                {customDealBeginsAt}
              </span>{' '}
            </p>
            <p>
              <Button type="link" onClick={onCustomDealClick}>
                See your deal details here
              </Button>
            </p>
            <p>
              <span>For more information, please contact </span>
              <Typography.Link strong href="mailto:growth@prerender.io" style={{ color: token.colorSuccess }}>
                growth@prerender.io
              </Typography.Link>
              <span style={secondaryTextStyle}>.</span>
            </p>
          </>
        )}
      </div>
      {showAccountDetails && (
        <>
          <ul style={detailsListStyle}>
            <li>
              <span>No. of renders: </span>

              <span style={strongStyle}>{createUsNumberFormat(user?.plan.limit)}</span>
            </li>
            {user.plan.current?.addonCostInCents != null && (
              <li>
                <span>Cost per 1000 extra renders: </span>
                <span style={strongStyle}>{formatCentsToDollarLocale(user.plan.current.addonCostInCents)}</span>
              </li>
            )}
            {user.plan.cacheRefreshInterval && (
              <li>
                <span>Cache freshness interval: </span>
                <span style={strongStyle}>{formatCacheRefreshInterval(user.plan.cacheRefreshInterval)}</span>
              </li>
            )}
          </ul>
          {shouldShowPackagesSection && (
            <>
              <h4 style={strongStyle}>Packages</h4>
              {packagesLoading && (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Spin />
                </div>
              )}
              {!packagesLoading && packagesError && (
                <Flex align="center" justify="center" style={{ height: '100%', width: '100%' }}>
                  <p style={{ color: 'red' }}>An unexpected error occured, please try again by reloading the page.</p>
                </Flex>
              )}
              {!packagesLoading && !packagesError && (
                <ul style={detailsListStyle}>
                  {activePackages.map((p) => {
                    return (
                      <li key={p.name}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>{p.name}</span>
                          <span>{formatToDollar(p.costInCents / 100)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </>
      )}
      {isInTrial && !!trialEndsAt && (
        <Flex style={{ borderTop: '1px solid #e8fae5', paddingTop: 16 }} justify="center">
          <TrialDaysStatistic trialEndsAt={trialEndsAt} />
        </Flex>
      )}

      {!isUserOnCustomPlan && (
        <Flex vertical align="center" style={{ marginTop: 16, width: '100%' }} gap={12}>
          {planActions.showActivateStarterNow && paymentMethodState.isChargeable && (
            <Typography.Text type="secondary" style={{ textAlign: 'center' }}>
              Your paid {user.planName} plan starts when your trial ends
              {trialEndsAt ? ` on ${dates.dayOfPointInTime(trialEndsAt)}` : ''}. Choose “Activate now” to start today
              instead.
            </Typography.Text>
          )}
          <CTABlockLoader loading={paymentMethodState.isLoading}>
            {planActions.showReactivate && (
              <ButtonWithRole
                type="primary"
                block
                onClick={onReactivatePlanClick}
                loading={subscriptionState.isLoading}
                disabled={subscriptionState.isLoading}
                disabledFor={BILLING_WRITE_REFUSED_ROLES}
              >
                Reactivate plan
              </ButtonWithRole>
            )}
            {planActions.showActivateStarterNow && (
              <ButtonWithRole
                onClick={onActivateStarterNowClick}
                disabledFor={BILLING_WRITE_REFUSED_ROLES}
                type="primary"
                block
              >
                Activate now
              </ButtonWithRole>
            )}
            {planActions.showKeepAccess && (
              <ButtonWithRole
                onClick={onKeepAccessClick}
                disabledFor={BILLING_WRITE_REFUSED_ROLES}
                type="primary"
                block
              >
                Keep access
              </ButtonWithRole>
            )}
            {planActions.showUpgrade && (
              <ButtonWithRole
                disabledFor={BILLING_WRITE_REFUSED_ROLES}
                type="primary"
                block
                onClick={() => onUpgradeLinkClick('Upgrade my plan')}
              >
                Upgrade my plan
              </ButtonWithRole>
            )}
            {planActions.showCancel && (
              <ButtonWithRole danger disabledFor={BILLING_WRITE_REFUSED_ROLES} block onClick={onCancelPlanClick}>
                Cancel my plan
              </ButtonWithRole>
            )}
          </CTABlockLoader>
        </Flex>
      )}
    </>
  );
};

function CTABlockLoader({ loading = false, children }) {
  if (loading) {
    return <Skeleton.Button active loading={loading} block />;
  }

  return children;
}
